import { plural } from './terms';

/**
 * What the COP says about a drone's state, and about a mission's.
 *
 * The vocabulary an operator reads most often, so it is the first section of the copy module
 * (#505 T8). Everything here was one of `FLYING`, `MAINT`, `A/C`, `COVERED - NO SORTIE`,
 * `TELEMETRY STALE` or `GPS DEGRADED · 6 SATS` before this file existed.
 *
 * CASE, per surface, because the two halves of this file follow different rules:
 *
 *  - `STATUS_LABEL` and `CHIP_STATE_LABEL` render through a CSS `uppercase` class
 *    (`StatusBadge.tsx`, `DroneSelectionChip.tsx:181`), so what is written here is sentence
 *    case and what appears on screen is unchanged. The capitals are the component's, not the
 *    string's.
 *  - `EXCEPTION_COPY` renders with NO `uppercase` class (`ExceptionPill`), so its case is
 *    literal. `docs/operational-standards.md` reserves capitals for a critical condition and
 *    asks for sentence case at warning and info, which is what this file finally does: today a
 *    warning-severity `TELEMETRY STALE` shouts exactly as loudly as a critical
 *    `NO TELEMETRY`, so the capitals carry no information at all. After this, they do.
 */

/**
 * `StatusBadge`'s vocabulary: drone states first, then mission states.
 *
 * Keys are the wire values and do not change. Only the words do.
 */
export const STATUS_LABEL = {
  flying: 'Flying',
  active: 'Active',
  online: 'Online',
  idle: 'Idle',
  charging: 'Charging',
  warning: 'Warning',
  /** Was `MAINT`. An abbreviation that saves five characters and costs a guess. */
  maintenance: 'Maintenance',
  offline: 'Offline',
  critical: 'Critical',

  draft: 'Draft',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  /**
   * Was `COVERED - NO SORTIE`.
   *
   * The two facts the old label carried, from `StatusBadge`'s own comment, are both kept: that
   * the capability is still attached and the mission still running ("Covered"), and that
   * nothing is up ("nothing flying"). An operator reading "In progress" over an empty station
   * has been told the wrong fact accurately, which is why this state exists at all - so the
   * replacement had to say both halves, not just the shorter one.
   *
   * "nothing in the air" was the first attempt and a test caught it: the same row's route-lock
   * notice reads "In the air, route locked" when a drone genuinely is up, so the phrase would
   * have meant "airborne" in one control and appeared inside its own negation in the control
   * beside it. "flying" is the word the rest of this file already uses for the same fact.
   *
   * "sortie" is retired product-wide: it is aviation register that has to be explained, and
   * the sentence that explained it is now simply the label.
   */
  covered_no_sortie: 'Covered, nothing flying',
  /**
   * The mission is running and the platform could not read whether anything is in the air (#471).
   *
   * IT NAMES THE PLATFORM'S LIMIT, NOT THE STATION'S STATE, because the station's state is
   * precisely what is unavailable. "Sortie state unknown" was the first wording and it reads as a
   * fact about the sky; this reads as a fact about the read, which is the honest one and the one
   * an operator can act on - the answer is to look at the aircraft, not at this badge.
   *
   * "COVERAGE UNREAD" AND NOT "SKY UNREAD", which is what this shipped as for one review cycle.
   * "Sky" was coined: neither "sky" nor "unread" appears in any operator-facing string in this
   * product, and this file's own precedent runs against coinage - it retired "sortie" as aviation
   * register that has to be explained. The established vocabulary for exactly this condition is on
   * the same panel - "Coverage unavailable", "Coverage degraded" - and the failed lookup IS the
   * coverage lookup, so "coverage unread" is both accurate and already in the operator's ear.
   *
   * ONE HEAD-TERM ACROSS ALL FOUR SURFACES that can show this state (badge, delete gate, terminal
   * lock, route-lock notice). They said three different things for one fact, which is the exact
   * inconsistency #471 is about.
   *
   * "In progress" still appears nowhere near it: the mission IS in progress, and saying so was
   * the original defect - telling an operator the wrong fact accurately.
   */
  coverage_unknown: 'Running, coverage unread',
  completed: 'Completed',
  /** US spelling, per the product's market and `settings/page.tsx`'s own "Checkout canceled". */
  cancelled: 'Canceled',
} as const;

export type StatusKey = keyof typeof STATUS_LABEL;

/**
 * `DroneSelectionChip`'s flight-state words - the one word on the map that says whether the
 * drone the operator just clicked is in the air.
 */
export const CHIP_STATE_LABEL = {
  flying: 'Flying',
  /** The drone's own `is_flying === false`. A fact, so the label may assert it. */
  grounded: 'On the ground',
  /**
   * Every other source, where "not flying" is still only `altitude <= 2 m`.
   *
   * Deliberately still the reading rather than a conclusion. "Not confirmed airborne" was
   * considered and rejected: it is longer, it fits worse in a marker-anchored chip, and it
   * throws away the number that lets an operator judge for themselves whether a MAVLink
   * drone hovering at 1.9 m is down or not. The chip's comment is emphatic that this state
   * must not claim more than the reading supports; naming the reading is the strongest form of
   * that.
   */
  below_bar: 'Below 2 m',
  /**
   * Was `DEGRADED`, which never said what had degraded.
   *
   * The chip renders this word specifically so a red-green colour-deficient operator is not
   * left with the stripe alone, so the word has to carry the whole meaning by itself.
   */
  stale: 'Link weak',
  lost: 'Link lost',
} as const;

export type ChipStateKey = keyof typeof CHIP_STATE_LABEL;

/**
 * The exception pill's conditions, in the operator's words.
 *
 * Functions rather than templates at the call site, with named parameters, so that when #505's
 * T9 turns these into ICU messages the call sites do not move. `critical` here means the same
 * thing it means in `operational-standards.md`: it shouts, and nothing else does.
 */
export const EXCEPTION_COPY = {
  /** Critical: the link is gone, so every other reading on the chip is a stale reading. */
  noTelemetry: ({ age }: { age: string }) => `NO TELEMETRY, ${age}`,
  /** Warning: the link is old but alive. */
  telemetryOld: ({ age }: { age: string }) => `Telemetry old, ${age}`,
  /**
   * Critical: below what it needs to fly home.
   *
   * "return reserve" rather than "RTH reserve". RTH survives as a command key an operator
   * presses and says out loud; it does not have to appear inside a sentence that has room for
   * the word it stands for.
   */
  batteryBelowReserve: ({ percent }: { percent: number }) =>
    `BATTERY ${percent}%, BELOW RETURN RESERVE`,
  /** Warning: still flyable, but the recovery has to be planned now rather than later. */
  batteryPlanRecovery: ({ percent }: { percent: number }) =>
    `Battery ${percent}%, plan the recovery`,
  /** Warning. Was `GPS DEGRADED · 6 SATS`; "sats" is not on the abbreviation allowlist. */
  gpsWeak: ({ satellites }: { satellites: number }) =>
    `GPS weak, ${plural(satellites, 'satellite', 'satellites')}`,
} as const;

/**
 * Which exception copy shouts, for the guard test to check against the case rule.
 *
 * Kept beside the copy rather than inferred from the string, so that a future edit which
 * accidentally lower-cases a critical condition fails instead of silently passing.
 */
export const CRITICAL_EXCEPTIONS: ReadonlySet<keyof typeof EXCEPTION_COPY> = new Set([
  'noTelemetry',
  'batteryBelowReserve',
]);

/**
 * "Coverage unread" — the head-term for a coverage fact the platform could not determine (#471),
 * and every sentence built on it (#650).
 *
 * `STATUS_LABEL.coverage_unknown` above argues at length for these two words and calls them "one
 * head-term across all four surfaces". They were then written as six independent string literals -
 * the badge, the delete gate, the launch lock, the terminal lock, and now two banners — so the
 * consistency that decision bought was one careless edit from being lost. It lives here now, and
 * `client/__tests__/lib/coverage-unread-copy.test.ts` holds the surfaces to it.
 *
 * THE CONDITION IS SHARED; THE IMPERATIVE IS NOT, and that is deliberate rather than drift. The
 * per-row sentences say "retry in a moment" because a transient lookup fault clears on the next
 * poll and the operator's move is to wait. The board banner says the same UNLESS every mission is
 * unread, which is the systemic case — and there the honest instruction is the opposite one: the
 * screen cannot answer at all, so the aircraft is the source. A single imperative would have been
 * wrong on one of the two.
 */
export const COVERAGE_UNREAD_TERM = 'Coverage unread';

export const COVERAGE_UNREAD_COPY = {
  /**
   * The board-level banner, which is the only thing that can state the SCOPE of the fault.
   *
   * A bare "coverage is unread" over a board where one row of sixty is affected would be as
   * misleading as the silence it replaces — the two faults the server can have are very different
   * sizes (a failed `attached` lookup clouds every row; a failed `held` lookup clouds only the
   * `in_progress` ones), and the count is what separates "a problem with that mission" from "the
   * platform cannot answer". So the count is in the sentence, not in the reasoning behind it.
   */
  boardNotice: ({ unread, total }: { unread: number; total: number }) =>
    (unread >= total
      ? `${COVERAGE_UNREAD_TERM} on all ${plural(total, 'mission', 'missions')} — trust the aircraft, not this list`
      : `${COVERAGE_UNREAD_TERM} on ${unread} of ${total} missions — retry in a moment`),
  /** The per-row delete gate and the launch locks. Transient by nature, so: wait. */
  retry: `${COVERAGE_UNREAD_TERM} — retry in a moment`,
  /**
   * The dock LAUNCH card, where the coverage question is "is there a station to check ALT
   * against", and the answer is that nobody could say.
   *
   * NOT "station altitude unavailable", which is the sibling sentence for a mission that
   * definitely HAS coverage whose station could not be read (#469). That one asserts a station
   * exists; borrowing it here would send an operator to a coverage panel that may hold nothing.
   *
   * No imperative, unlike its siblings. The notice renders directly under the ALT field on a card
   * whose next press launches an aircraft: naming the check that did not run, beside the control
   * that would have been checked, IS the instruction, and a fourth clause at 10 px buys nothing.
   */
  stationNotCheckable: `${COVERAGE_UNREAD_TERM} — ALT was not checked against a station.`,
  /** #469's sibling: coverage IS attached, and `/api/coverage/:id` could not say where. */
  stationUnavailable: 'Station altitude unavailable — ALT was not checked against it.',
} as const;
